import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { MagnifyingGlassIcon, QueueListIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  badgeClass,
  Field,
  FormActions,
  formatMoney,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import {
  useBookJobCard,
  useOperationsPage,
  useOperationSummary,
  useRemoveJobCard,
  useWorkCenterOptions,
  useWorkOrderOperations,
} from '../../hooks/useRouting';
import { OperationStatus, WorkOrderOperation } from '../../services/routing.service';
import { usePermissions } from '../../hooks/usePermissions';
import { useActiveScrapReasons } from './Scrap';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';

const PAGE_SIZES = [10, 25, 50, 100];
const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';

const STATUS: Record<OperationStatus, { label: string; className: string }> = {
  pending: { label: 'Not started', className: 'bg-gray-50 text-gray-700 ring-gray-500/20' },
  in_progress: { label: 'Running', className: 'bg-blue-50 text-blue-700 ring-blue-600/20' },
  completed: { label: 'Done', className: 'bg-green-50 text-green-700 ring-green-600/20' },
};
const StatusBadge: React.FC<{ status: OperationStatus }> = ({ status }) => <span className={cn(badgeClass, STATUS[status]?.className)}>{STATUS[status]?.label ?? status}</span>;

const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
const formatDate = (value?: string | null) =>
  value ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const formatMinutes = (minutes: number) => {
  const value = Number(minutes) || 0;
  if (value < 60) return `${formatNumber(value, 2)} min`;
  return `${Math.floor(value / 60)} h ${formatNumber(Math.round(value % 60))} min`;
};

interface BookingPanelProps {
  workOrderId: number;
  operationId: string;
  canBook: boolean;
  onClose: () => void;
}

// One work order on the shop floor: its steps, a card to book on the chosen step, and the cards so far
const BookingPanel: React.FC<BookingPanelProps> = ({ workOrderId, operationId, canBook, onClose }) => {
  const { data, isLoading, isError, error } = useWorkOrderOperations(workOrderId);
  const bookJobCard = useBookJobCard();
  const removeJobCard = useRemoveJobCard();
  const { data: scrapReasons = [] } = useActiveScrapReasons();

  const [selectedId, setSelectedId] = useState(operationId);
  const [form, setForm] = useState({ workDate: today(), operatorName: '', minutes: '', goodQuantity: '', rejectedQuantity: '', reworkQuantity: '', scrapReasonId: '', rejectionReason: '', notes: '' });
  // A rework card books what came out of rework; an ordinary card books new work
  const [reworkCard, setReworkCard] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  if (isLoading) return <p className="py-10 text-center text-sm text-gray-500">Loading…</p>;
  if (isError || !data) return <p className="py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The work order could not be loaded.')}</p>;

  const operation = data.operations.find((candidate) => candidate.id === selectedId) ?? data.operations[0];
  const open = data.status === 'in_progress';
  const inRework = operation?.inReworkQuantity ?? 0;
  const fromRework = reworkCard && inRework > 0;
  const set = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    setErrors((prev) => {
      const { [field]: _cleared, ...rest } = prev;
      return rest;
    });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!operation) return;
    const minutes = form.minutes.trim() === '' ? 0 : Number(form.minutes);
    const good = form.goodQuantity.trim() === '' ? 0 : Number(form.goodQuantity);
    const rejected = form.rejectedQuantity.trim() === '' ? 0 : Number(form.rejectedQuantity);
    const rework = fromRework || form.reworkQuantity.trim() === '' ? 0 : Number(form.reworkQuantity);
    const next: Record<string, string> = {};
    if (!Number.isFinite(minutes) || minutes < 0) next.minutes = 'Enter the minutes worked';
    if (!Number.isInteger(good) || good < 0) next.goodQuantity = 'Whole number';
    if (!Number.isInteger(rejected) || rejected < 0) next.rejectedQuantity = 'Whole number';
    if (!Number.isInteger(rework) || rework < 0) next.reworkQuantity = 'Whole number';
    if (!next.goodQuantity && !next.rejectedQuantity && !next.reworkQuantity) {
      if (fromRework && good + rejected > inRework) next.goodQuantity = `Only ${formatNumber(inRework)} in rework`;
      else if (!fromRework && good + rejected + rework > operation.remainingQuantity) next.goodQuantity = `Only ${formatNumber(operation.remainingQuantity)} left on this step`;
    }
    if (rejected > 0 && !form.scrapReasonId && !form.rejectionReason.trim()) next.rejectionReason = 'Choose a reason or write one';
    if (Object.keys(next).length === 0 && minutes === 0 && good + rejected + rework === 0) next.minutes = 'Enter the time worked or a quantity';
    if (!form.workDate) next.workDate = 'Enter the date';
    else if (form.workDate > today()) next.workDate = 'Not in the future';
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    bookJobCard.mutate(
      {
        workOrderId,
        operationId: operation.id,
        data: {
          workDate: form.workDate,
          operatorName: form.operatorName.trim() || null,
          minutes,
          goodQuantity: good,
          rejectedQuantity: rejected,
          reworkQuantity: rework,
          rejectionReason: rejected > 0 ? form.rejectionReason.trim() || null : null,
          scrapReasonId: rejected > 0 ? form.scrapReasonId || null : null,
          fromRework,
          notes: form.notes.trim() || null,
        },
      },
      {
        onSuccess: () => {
          toast.success(fromRework ? `Rework booked on ${operation.name}` : `Booked on ${operation.name}`);
          setForm((prev) => ({ ...prev, minutes: '', goodQuantity: '', rejectedQuantity: '', reworkQuantity: '', scrapReasonId: '', rejectionReason: '', notes: '' }));
          setReworkCard(false);
        },
        onError: (submitError) => toast.error(getApiErrorMessage(submitError, 'The job card could not be booked')),
      },
    );
  };

  const handleRemove = (jobCardId: string) => {
    removeJobCard.mutate(
      { workOrderId, jobCardId },
      {
        onSuccess: () => toast.success('Job card removed'),
        onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'The job card could not be removed')),
      },
    );
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-gray-50 p-4 ring-1 ring-gray-200">
        <div>
          <p className="text-sm font-semibold text-gray-900">{data.productName}</p>
          <p className="text-xs text-gray-500">
            {data.sku} · planned {formatNumber(data.plannedQuantity)} {data.unit}
          </p>
        </div>
        <dl className="flex gap-6 text-right text-sm">
          <div>
            <dt className="text-xs uppercase tracking-wide text-gray-500">Time</dt>
            <dd className="font-semibold text-gray-900 tabular-nums">
              {formatMinutes(data.actualMinutes)} <span className="font-normal text-gray-500">of {formatMinutes(data.plannedMinutes)}</span>
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-gray-500">Cost</dt>
            <dd className="font-semibold text-gray-900 tabular-nums">
              {formatMoney(data.actualCost)} <span className="font-normal text-gray-500">of {formatMoney(data.plannedCost)}</span>
            </dd>
          </div>
        </dl>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className={thClass}>Operation</th>
              <th scope="col" className={cn(thClass, 'text-right')}>To work on</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Good</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Rejected</th>
              <th scope="col" className={cn(thClass, 'text-right')}>In rework</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Left</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Time</th>
              <th scope="col" className={thClass}>Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.operations.map((row) => (
              <tr
                key={row.id}
                onClick={() => setSelectedId(row.id)}
                className={cn('cursor-pointer', row.id === operation?.id ? 'bg-indigo-50' : 'hover:bg-gray-50')}
                aria-selected={row.id === operation?.id}
              >
                <td className="px-4 py-2.5">
                  <p className="text-sm font-medium text-gray-900">
                    {row.sequence} · {row.name}
                  </p>
                  <p className="text-xs text-gray-500">{row.isOutsourced ? 'Outside job work' : row.workCenterName ?? 'No work center'}</p>
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-700 tabular-nums">{formatNumber(row.inputQuantity)}</td>
                <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm font-medium text-gray-900 tabular-nums">{formatNumber(row.goodQuantity)}</td>
                <td className={cn('whitespace-nowrap px-4 py-2.5 text-right text-sm tabular-nums', row.rejectedQuantity > 0 ? 'font-medium text-red-600' : 'text-gray-400')}>
                  {row.rejectedQuantity > 0 ? formatNumber(row.rejectedQuantity) : '—'}
                </td>
                <td className={cn('whitespace-nowrap px-4 py-2.5 text-right text-sm tabular-nums', row.inReworkQuantity > 0 ? 'font-medium text-amber-700' : 'text-gray-400')}>
                  {row.inReworkQuantity > 0 ? formatNumber(row.inReworkQuantity) : '—'}
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-700 tabular-nums">{formatNumber(row.remainingQuantity)}</td>
                <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-700 tabular-nums">{formatMinutes(row.actualMinutes)}</td>
                <td className="whitespace-nowrap px-4 py-2.5">
                  <StatusBadge status={row.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {operation && open && canBook ? (
        <form onSubmit={handleSubmit} noValidate className="rounded-lg border border-gray-200 bg-white">
          <div className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-3">
            <h4 className="text-sm font-semibold text-gray-900">
              Book on {operation.sequence} · {operation.name}
            </h4>
            <p className="mt-0.5 text-xs text-gray-500">
              {fromRework
                ? `${formatNumber(inRework)} ${data.unit} are in rework. Book how many came out good and how many are scrap; the rest stay in rework.`
                : `${formatNumber(operation.remainingQuantity)} ${data.unit} can still be booked as good, rejected or for rework. Click another step above to book on it.`}
            </p>
            {inRework > 0 && (
              <div className="mt-2 inline-flex rounded-md shadow-sm" role="group" aria-label="Kind of job card">
                <button type="button" aria-pressed={!fromRework} onClick={() => setReworkCard(false)} className={cn('rounded-l-md px-3 py-1.5 text-xs font-semibold ring-1 ring-inset', !fromRework ? 'bg-indigo-600 text-white ring-indigo-600' : 'bg-white text-gray-700 ring-gray-300 hover:bg-gray-50')}>
                  New work
                </button>
                <button type="button" aria-pressed={fromRework} onClick={() => setReworkCard(true)} className={cn('-ml-px rounded-r-md px-3 py-1.5 text-xs font-semibold ring-1 ring-inset', fromRework ? 'bg-amber-600 text-white ring-amber-600' : 'bg-white text-gray-700 ring-gray-300 hover:bg-gray-50')}>
                  Out of rework ({formatNumber(inRework)})
                </button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 p-4 lg:grid-cols-6">
            <Field label="Date" htmlFor="jc-date" error={errors.workDate}>
              <input id="jc-date" type="date" max={today()} value={form.workDate} onChange={set('workDate')} className={cn(inputClass, errors.workDate && inputErrorClass)} />
            </Field>
            <Field label="Operator" htmlFor="jc-operator">
              <input id="jc-operator" type="text" maxLength={100} value={form.operatorName} onChange={set('operatorName')} className={inputClass} />
            </Field>
            <Field label="Minutes" htmlFor="jc-minutes" error={errors.minutes}>
              <input id="jc-minutes" type="number" min="0" step="0.01" value={form.minutes} onChange={set('minutes')} className={cn(inputClass, 'text-right tabular-nums', errors.minutes && inputErrorClass)} placeholder="0" />
            </Field>
            <Field label="Good" htmlFor="jc-good" error={errors.goodQuantity}>
              <input id="jc-good" type="number" min="0" step="1" value={form.goodQuantity} onChange={set('goodQuantity')} className={cn(inputClass, 'text-right tabular-nums', errors.goodQuantity && inputErrorClass)} placeholder="0" />
            </Field>
            <Field label="Rejected" htmlFor="jc-rejected" error={errors.rejectedQuantity}>
              <input id="jc-rejected" type="number" min="0" step="1" value={form.rejectedQuantity} onChange={set('rejectedQuantity')} className={cn(inputClass, 'text-right tabular-nums', errors.rejectedQuantity && inputErrorClass)} placeholder="0" />
            </Field>
            <Field label="For rework" htmlFor="jc-rework" error={errors.reworkQuantity}>
              <input id="jc-rework" type="number" min="0" step="1" disabled={fromRework} value={fromRework ? '' : form.reworkQuantity} onChange={set('reworkQuantity')} className={cn(inputClass, 'text-right tabular-nums', errors.reworkQuantity && inputErrorClass)} placeholder={fromRework ? '—' : '0'} />
            </Field>
            {Number(form.rejectedQuantity) > 0 && (
              <>
                <Field label="Scrap reason" htmlFor="jc-scrap-reason" className="col-span-2 lg:col-span-3">
                  <select id="jc-scrap-reason" value={form.scrapReasonId} onChange={set('scrapReasonId')} className={cn(inputClass, errors.rejectionReason && !form.scrapReasonId && inputErrorClass)}>
                    <option value="">{scrapReasons.length === 0 ? 'No reasons set up; write one' : 'Choose…'}</option>
                    {scrapReasons.map((reason) => (
                      <option key={reason.id} value={reason.id}>
                        {reason.code} · {reason.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label={form.scrapReasonId ? 'More about it' : 'Reason in words'} htmlFor="jc-reason" error={errors.rejectionReason} className="col-span-2 lg:col-span-3">
                  <input id="jc-reason" type="text" maxLength={200} value={form.rejectionReason} onChange={set('rejectionReason')} className={cn(inputClass, errors.rejectionReason && inputErrorClass)} />
                </Field>
              </>
            )}
            <Field label="Notes" htmlFor="jc-notes" className="col-span-2 lg:col-span-6">
              <input id="jc-notes" type="text" maxLength={500} value={form.notes} onChange={set('notes')} className={inputClass} />
            </Field>
          </div>
          <div className="flex justify-end border-t border-gray-200 px-4 py-3">
            <button type="submit" disabled={bookJobCard.isPending} className={primaryButtonClass}>
              {bookJobCard.isPending ? 'Booking…' : fromRework ? 'Book Rework Card' : 'Book Job Card'}
            </button>
          </div>
        </form>
      ) : (
        !open && (
          <p className="rounded-md bg-gray-50 px-4 py-3 text-sm text-gray-700 ring-1 ring-gray-200">
            {data.status === 'draft' || data.status === 'planned'
              ? 'Start the work order to book job cards.'
              : `This work order is ${data.status.replace('_', ' ')}; its job cards are closed.`}
          </p>
        )
      )}

      <section className="rounded-lg border border-gray-200">
        <h4 className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
          Job cards · {formatNumber(data.jobCards.length)}
        </h4>
        {data.jobCards.length === 0 ? (
          <p className="px-4 py-4 text-sm text-gray-500">Nothing has been booked yet.</p>
        ) : (
          <ul className="max-h-64 divide-y divide-gray-100 overflow-y-auto">
            {data.jobCards.map((card) => (
              <li key={card.id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                <div className="min-w-0">
                  <p className="font-medium text-gray-900">
                    {card.sequence} · {card.operationName}
                    {card.fromRework && <span className={cn(badgeClass, 'ml-2 bg-amber-50 text-amber-800 ring-amber-600/20')}>Rework</span>}
                    <span className="ml-2 font-normal text-gray-500">
                      {formatDate(card.workDate)}
                      {card.operatorName ? ` · ${card.operatorName}` : ''}
                    </span>
                  </p>
                  <p className="text-xs text-gray-600">
                    {formatMinutes(card.minutes)} · {formatNumber(card.goodQuantity)} good
                    {card.rejectedQuantity > 0 && <span className="text-red-600"> · {formatNumber(card.rejectedQuantity)} rejected ({card.rejectionReason})</span>}
                    {card.reworkQuantity > 0 && ` · ${formatNumber(card.reworkQuantity)} for rework`}
                    {card.notes ? ` · ${card.notes}` : ''}
                  </p>
                </div>
                {open && canBook && (
                  <button
                    type="button"
                    onClick={() => handleRemove(card.id)}
                    disabled={removeJobCard.isPending}
                    title="Remove this job card"
                    aria-label={`Remove the job card on ${card.operationName}`}
                    className="shrink-0 rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <FormActions>
        <button type="button" onClick={onClose} className={secondaryButtonClass}>
          Close
        </button>
      </FormActions>
    </div>
  );
};

const JobCards: React.FC = () => {
  const { can } = usePermissions();
  const canBook = can('manufacturing.update');

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<OperationStatus | ''>('');
  const [workCenterFilter, setWorkCenterFilter] = useState('');
  const [scope, setScope] = useState<'open' | 'all'>('open');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultRowsPerPage);
  const [booking, setBooking] = useState<WorkOrderOperation | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isError, error, isFetching, refetch } = useOperationsPage({
    page,
    limit,
    search: search || undefined,
    status: statusFilter,
    workCenterId: workCenterFilter || undefined,
    openOnly: scope === 'open',
  });
  const { data: summary } = useOperationSummary();
  const { data: workCenters = [] } = useWorkCenterOptions();

  const operations = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  useEffect(() => {
    if (data && page > data.totalPages) setPage(data.totalPages);
  }, [data, page]);

  const hasFilters = !!searchInput || !!statusFilter || !!workCenterFilter || scope !== 'open';
  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setStatusFilter('');
    setWorkCenterFilter('');
    setScope('open');
    setPage(1);
  };

  const stats = [
    { label: 'Work orders running', value: summary ? formatNumber(summary.workOrdersInProgress) : '—', hint: 'With operations to book' },
    { label: 'Open operations', value: summary ? formatNumber(summary.openOperations) : '—', hint: `${summary ? formatNumber(summary.runningOperations) : '—'} running now` },
    { label: 'Booked today', value: summary ? `${formatNumber(summary.hoursBookedToday, 1)} h` : '—', hint: 'On job cards' },
    { label: 'Rejected', value: summary ? formatNumber(summary.rejectedUnits) : '—', hint: 'Unit(s) on running work orders' },
  ];
  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Job Cards</h1>
          <p className="mt-1 text-sm text-gray-500">
            The operations of work orders in progress. Book the time worked and the good, rejected and rework quantities step by step.
          </p>
        </div>
        <Link to="/routings" className={secondaryButtonClass}>
          <QueueListIcon className="-ml-0.5 mr-2 h-4 w-4" />
          Routings
        </Link>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search by work order, product or operation" aria-label="Search operations" className={cn(inputClass, 'pl-9')} />
          </div>
          <select value={scope} onChange={(event) => { setScope(event.target.value as 'open' | 'all'); setPage(1); }} aria-label="Work orders" className={cn(inputClass, 'lg:w-52')}>
            <option value="open">Work orders in progress</option>
            <option value="all">All work orders</option>
          </select>
          <select value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value as OperationStatus | ''); setPage(1); }} aria-label="Status" className={cn(inputClass, 'lg:w-40')}>
            <option value="">All statuses</option>
            <option value="pending">Not started</option>
            <option value="in_progress">Running</option>
            <option value="completed">Done</option>
          </select>
          <select value={workCenterFilter} onChange={(event) => { setWorkCenterFilter(event.target.value); setPage(1); }} aria-label="Work center" className={cn(inputClass, 'lg:w-52')}>
            <option value="">All work centers</option>
            {workCenters.map((workCenter) => (
              <option key={workCenter.id} value={workCenter.id}>
                {workCenter.name}
              </option>
            ))}
          </select>
          {hasFilters && (
            <button type="button" onClick={clearFilters} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
              Clear
            </button>
          )}
        </div>

        {isError ? (
          <div className="p-10 text-center">
            <p className="text-sm text-gray-700">{getApiErrorMessage(error, 'The operations could not be loaded.')}</p>
            <button type="button" onClick={() => refetch()} className={cn(secondaryButtonClass, 'mt-4')}>
              Try again
            </button>
          </div>
        ) : (
          <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={thClass}>Work order</th>
                  <th scope="col" className={thClass}>Operation</th>
                  <th scope="col" className={thClass}>Work center</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Good</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Rejected</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Left</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Time</th>
                  <th scope="col" className={thClass}>Status</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {isLoading ? (
                  Array.from({ length: 4 }).map((_, index) => (
                    <tr key={index}>
                      {Array.from({ length: 9 }).map((__, cell) => (
                        <td key={cell} className="px-4 py-4">
                          <div className="h-4 animate-pulse rounded bg-gray-100" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : operations.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center">
                      <p className="text-sm font-medium text-gray-900">{hasFilters ? 'No operation matches' : 'No operations to book'}</p>
                      <p className="mt-1 text-sm text-gray-500">
                        {hasFilters ? 'Try a different search or filter.' : 'Operations appear here when a work order of a product with a routing is started.'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  operations.map((operation) => (
                    <tr key={operation.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">{operation.workOrderNumber}</p>
                        <p className="mt-0.5 text-xs text-gray-500">
                          {operation.productName} · {formatNumber(operation.plannedQuantity)} {operation.unit}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {operation.sequence} · {operation.name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{operation.isOutsourced ? 'Outside job work' : operation.workCenterName ?? <span className="text-gray-400">—</span>}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900 tabular-nums">{formatNumber(operation.goodQuantity)}</td>
                      <td className={cn('whitespace-nowrap px-4 py-3 text-right text-sm tabular-nums', operation.rejectedQuantity > 0 ? 'font-medium text-red-600' : 'text-gray-400')}>
                        {operation.rejectedQuantity > 0 ? formatNumber(operation.rejectedQuantity) : '—'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{formatNumber(operation.remainingQuantity)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">
                        {formatMinutes(operation.actualMinutes)}
                        <span className="block text-xs text-gray-500">of {formatMinutes(operation.plannedMinutes)}</span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <StatusBadge status={operation.status} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => setBooking(operation)}
                          className="rounded-md px-2.5 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50"
                          aria-label={`Open ${operation.name} of ${operation.workOrderNumber}`}
                        >
                          {operation.workOrderStatus === 'in_progress' && canBook ? 'Book' : 'View'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
            <span>
              Showing {firstRow}–{lastRow} of {formatNumber(total)}
            </span>
            <label className="flex items-center gap-2">
              Rows per page
              <select
                value={limit}
                onChange={(event) => {
                  setLimit(Number(event.target.value));
                  setPage(1);
                }}
                className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
        </div>
      </div>

      <Modal isOpen={!!booking} onClose={() => setBooking(null)} title={booking?.workOrderNumber ?? 'Job Cards'} size="2xl">
        {booking && <BookingPanel key={booking.id} workOrderId={booking.workOrderId} operationId={booking.id} canBook={canBook} onClose={() => setBooking(null)} />}
      </Modal>
    </div>
  );
};

export default JobCards;
